<template>
  <b-card header-tag="header">
    <template v-slot:header>
      <h6 class="mb-0" v-if="showName">Monitor Configuration ({{ getSafe(() => device.object) }})</h6>
      <h6 class="mb-0" v-else>Monitor Configuration</h6>
    </template>

    <template v-if="loading">
      <div class="d-flex justify-content-center mb-3">
        <b-spinner variant="primary"></b-spinner>
      </div>
    </template>

    <div v-else>
      <b-alert show variant="warning" class="py-2 px-3">
        These settings re-scale the readings. Use them to match the physical sensor you installed
        (or to rule out a wrong phase count). Changes apply live on the device.
      </b-alert>

      <!-- sensor model -->
      <b-form-group class="mb-3">
        <template v-slot:label>
          <span class="font-weight-bold">Current sensor</span>
          <small class="text-muted d-block">Pick the clamp model you installed on this device.</small>
        </template>
        <b-form-select
          v-model="sensor"
          :options="sensorOptions"
          :disabled="sending"
          @change="onSensorChange"
        ></b-form-select>
      </b-form-group>

      <!-- phases -->
      <b-form-group class="mb-3">
        <template v-slot:label>
          <span class="font-weight-bold">Phases</span>
          <small class="text-muted d-block">
            1 = single line (~120V), 2 = split-phase (~220V), 3 = three-phase.
          </small>
        </template>
        <b-form-radio-group
          v-model="phase"
          :options="phaseOptions"
          buttons
          button-variant="outline-secondary"
          :disabled="sending"
          @change="onPhaseChange"
        ></b-form-radio-group>
      </b-form-group>

      <!-- per-phase voltage -->
      <b-form-group class="mb-3">
        <template v-slot:label>
          <span class="font-weight-bold">Line voltage (per phase)</span>
          <small class="text-muted d-block">
            Volts line-to-neutral of ONE phase (~110–127), NOT line-to-line (220/330).
            Total power = sum of phase currents × this. Default 126.
          </small>
        </template>
        <b-input-group append="V" style="max-width: 170px;">
          <b-form-input
            type="number"
            v-model.number="voltage"
            :disabled="sending"
            min="80"
            max="400"
            @change="onVoltageChange"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <!-- device name / nickname -->
      <b-form-group class="mb-3">
        <template v-slot:label>
          <span class="font-weight-bold">Device name</span>
          <small class="text-muted d-block">Shown on the device screen ("connected to …").</small>
        </template>
        <b-input-group>
          <b-form-input
            v-model="name"
            :disabled="sending"
            maxlength="24"
            placeholder="e.g. Kitchen fridge"
            @keyup.enter="onNameSave"
          ></b-form-input>
          <b-input-group-append>
            <b-button variant="primary" :disabled="sending || !name.trim()" @click="onNameSave">
              <b-spinner small v-show="sendingName"></b-spinner>
              Save
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <hr class="my-3" />

      <div class="d-flex justify-content-between align-items-center mb-3">
        <span class="font-weight-bold">Restart</span>
        <b-button variant="outline-danger" size="sm" :disabled="sending" @click="onReboot">
          <b-spinner small v-show="sendingReboot"></b-spinner>
          Reboot device
        </b-button>
      </div>

      <hr class="my-3" />

      <p class="text-muted small mb-0">
        Applies live on the device. After each change the monitor reports its state back
        (zero offsets + sensor + phase) via its <code>set_cal</code> message — visible in the device log.
      </p>
    </div>
  </b-card>
</template>

<script>
import { BROADCAST_ACTION } from "@/core/services/store/devices.module";

export default {
  name: "suitch-enermon-config-widget",
  props: {
    device: {
      type: Object,
      required: true,
    },
    showName: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      sendingSensor: false,
      sendingPhase: false,
      sendingVoltage: false,
      sendingName: false,
      sendingReboot: false,
      sensor: 30,
      phase: 2,
      voltage: 126,
      name: "",
      sensorOptions: [
        { text: "30A / 1V (SCT-013-030)", value: 30 },
        { text: "30A / 333mV (black)", value: 33 },
        { text: "100A (SCT-013-000)", value: 100 },
      ],
      phaseOptions: [
        { text: "1 phase", value: 1 },
        { text: "2 phases", value: 2 },
        { text: "3 phases", value: 3 },
      ],
    };
  },
  computed: {
    sending() {
      return (
        this.sendingSensor ||
        this.sendingPhase ||
        this.sendingVoltage ||
        this.sendingName ||
        this.sendingReboot
      );
    },
  },
  mounted() {
    // seed the controls from the device record if it carries these fields
    if (this.device) {
      if (this.device.sensor) this.sensor = Number(this.device.sensor);
      if (this.device.phase) this.phase = Number(this.device.phase);
      if (this.device.voltage) this.voltage = Number(this.device.voltage);
      if (this.device.object) this.name = this.device.object;
    }
    setTimeout(() => {
      this.loading = false;
    }, 400);
  },
  methods: {
    getSafe(fn) {
      try {
        return fn();
      } catch (e) {
        return "";
      }
    },
    sensorLabel(v) {
      const o = this.sensorOptions.find((x) => x.value === v);
      return o ? o.text : v;
    },
    onSensorChange(value) {
      this.sendingSensor = true;
      this.sendCommand("set_sensor:" + value)
        .then(() => {
          this.$bvToast.toast("Sensor set to " + this.sensorLabel(value), {
            title: "Monitor Configuration",
            variant: "success",
            solid: true,
          });
        })
        .finally(() => {
          this.sendingSensor = false;
        });
    },
    onPhaseChange(value) {
      this.sendingPhase = true;
      this.sendCommand("set_phase:" + value)
        .then(() => {
          this.$bvToast.toast("Phases set to " + value, {
            title: "Monitor Configuration",
            variant: "success",
            solid: true,
          });
        })
        .finally(() => {
          this.sendingPhase = false;
        });
    },
    onVoltageChange(value) {
      const v = parseInt(value, 10);
      if (isNaN(v) || v < 80 || v > 400) {
        this.$bvToast.toast("Voltage must be between 80 and 400 V", {
          title: "Monitor Configuration",
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.sendingVoltage = true;
      this.sendCommand("set_voltage:" + v)
        .then(() => {
          this.$bvToast.toast("Per-phase voltage set to " + v + " V", {
            title: "Monitor Configuration",
            variant: "success",
            solid: true,
          });
        })
        .finally(() => {
          this.sendingVoltage = false;
        });
    },
    onNameSave() {
      const n = (this.name || "").trim();
      if (!n) return;
      this.sendingName = true;
      this.sendCommand("set_name:" + n)
        .then(() => {
          this.$bvToast.toast('Device name set to "' + n + '"', {
            title: "Monitor Configuration",
            variant: "success",
            solid: true,
          });
        })
        .finally(() => {
          this.sendingName = false;
        });
    },
    onReboot() {
      this.$bvModal
        .msgBoxConfirm(
          "Reboot the device now? It will go offline for a few seconds while it restarts.",
          {
            title: "Reboot device",
            okTitle: "Reboot",
            cancelTitle: "Cancel",
            okVariant: "danger",
          }
        )
        .then((ok) => {
          if (!ok) return;
          this.sendingReboot = true;
          this.sendCommand("reboot")
            .then(() => {
              this.$bvToast.toast("Reboot command sent", {
                title: "Monitor Configuration",
                variant: "warning",
                solid: true,
              });
            })
            .finally(() => {
              this.sendingReboot = false;
            });
        });
    },
    sendCommand(commandData) {
      return this.$store
        .dispatch(BROADCAST_ACTION, {
          token: this.device.token,
          secret: this.device.secret_token,
          command_data: commandData,
        })
        .catch((error) => {
          console.error("Error sending monitor command:", error);
          this.$bvToast.toast(`Failed to send "${commandData}" to the monitor`, {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          throw error;
        });
    },
  },
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}
</style>
